import { useCallback, useRef } from 'react';

import { trackingAdapter } from 'src/app/shared/dataAnalytics/analyticsAdapterFactory';
import { retrieveTrackingData } from 'src/app/shared/dataAnalytics/storage';
import { useProductSetFromCart } from 'src/app/shared/hooks/useProductSetFromCart';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { deriveTrackingAttributes } from 'src/app/v2/utils/deriveTrackingAttributes';
import { getFromReduxPersist } from 'src/app/v2/utils/getFromReduxPersist';
import { useHirerAccountAndUserDataQuery } from 'src/graphql/generated';

import { useDraft } from '../useDraft';
import { usePage } from '../usePage';

export const useAnalyticsTracking = () => {
  const page = usePage();
  const trackingAdapterRef = useRef<ReturnType<typeof trackingAdapter>>();

  const draft = useDraft();
  const { cartState, orderState } = useShoppingCart();
  const productSet = useProductSetFromCart({ draftId: draft?.id });

  const { data: hirerData } = useHirerAccountAndUserDataQuery({
    variables: {
      advertiserNation: 'deprecated',
    },
    fetchPolicy: 'cache-only',
  });

  const getMergedState = useCallback(() => {
    // TODO: Remove this when all pages have been migrated and types cleaned up
    const trackingDataFromRedux = getFromReduxPersist();
    if (!draft || !hirerData) return trackingDataFromRedux;
    // This contains the shared tracking data that is the result of hirer interaction
    // and needs to fire across pages like the success page
    // ie isPaidWithCreditCard, paymentMethod, etc
    const trackingDataFromSessionStorage = retrieveTrackingData();
    // This contains tracking data derived from draft and hirer data
    const trackingDataFromQuery = deriveTrackingAttributes(
      draft,
      hirerData,
      page,
      cartState,
      productSet,
      orderState,
    );

    return {
      ...trackingDataFromRedux,
      ...trackingDataFromQuery,
      ...trackingDataFromSessionStorage,
      page,
    };
  }, [page, draft, hirerData, cartState, productSet, orderState]);

  if (!trackingAdapterRef.current) {
    trackingAdapterRef.current = trackingAdapter({
      getTrackingData: getMergedState,
    });
  }

  return {
    trackingAdapter: trackingAdapterRef.current,
    getStore: getMergedState,
  };
};
